import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { SigmaContainer } from '@react-sigma/core';
import '@react-sigma/core/lib/style.css';
import Graph from 'graphology';
import { useGraphStore } from '@/stores/graph';
import {
  GraphControlsPanel,
  GraphSearchBar,
  NodeDetailPanel,
  GraphToolbar,
  GraphStatusBar,
  GraphDataController,
} from '@/components/graph';
import { Maximize2 } from 'lucide-react';

const SIGMA_SETTINGS = {
  allowInvalidContainer: true,
  renderLabels: true,
  renderEdgeLabels: false,
  labelFont: 'Inter, system-ui, sans-serif',
  labelSize: 11,
  labelWeight: '500',
  labelColor: { color: '#e2e8f0' },
  labelDensity: 0.6,
  labelGridCellSize: 100,
  labelRenderedSizeThreshold: 6,
  edgeLabelFont: 'Inter, system-ui, sans-serif',
  edgeLabelSize: 9,
  stagePadding: 40,
  defaultNodeType: 'circle',
  defaultEdgeType: 'line',
  zIndex: true,
  minCameraRatio: 0.02,
  maxCameraRatio: 20,
  defaultNodeColor: '#94a3b8',
  defaultEdgeColor: '#334155',
  autoRescale: true,
  autoCenter: true,
} as const;

/**
 * CSS overrides for the sigma container background.
 * react-sigma's CSS uses CSS variables for colors.
 */
const SIGMA_CSS_VARS = {
  '--sigma-background-color': '#0f172a',
} as React.CSSProperties;

function SigmaContent() {
  const { snapshot, filters, selectedNodeId, detailPanelOpen } = useGraphStore();

  return (
    <>
      <GraphDataController snapshot={snapshot} layout={filters.layout} />
      <GraphSearchBar />
      <GraphToolbar />
      <GraphStatusBar />
      {detailPanelOpen && selectedNodeId && <NodeDetailPanel />}
    </>
  );
}

function GraphEmptyState() {
  return (
    <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10">
      <div className="text-center text-slate-500">
        <svg
          className="w-16 h-16 mx-auto mb-3 text-slate-600"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1}
            d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
          />
        </svg>
        <p className="text-sm font-medium">No graph data</p>
        <p className="text-xs mt-1">Select a client or adjust filters to load</p>
      </div>
    </div>
  );
}

export function GraphPage() {
  const {
    snapshot,
    error,
    loadSnapshot,
    loadStats,
    focusOnNode,
    fullscreen,
    setFullscreen,
  } = useGraphStore();

  const [searchParams, setSearchParams] = useSearchParams();
  const focusId = searchParams.get('focus');
  const focusClient = searchParams.get('client');

  useEffect(() => {
    if (focusId && focusClient) {
      // Deep-link entry: focus the graph on a specific node (e.g. a buyer
      // scenario from the scenario browser) and expand its neighborhood so
      // linked entities are visible immediately.
      focusOnNode(focusClient, focusId).finally(() => {
        loadStats();
        // Clear the query params so a user refresh doesn't re-trigger the
        // focus flow (we keep the selected node in store instead).
        setSearchParams({}, { replace: true });
      });
      return;
    }
    loadSnapshot();
    loadStats();
    // Intentionally only on mount — the store drives subsequent reloads.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hasData = snapshot && snapshot.nodes.length > 0;

  if (fullscreen) {
    return (
      <div className="fixed inset-0 z-50 flex bg-slate-900">
        <GraphControlsPanel />
        <div className="flex-1 relative min-w-0" style={SIGMA_CSS_VARS}>
          {!hasData && <GraphEmptyState />}
          <SigmaContainer
            graph={Graph}
            settings={SIGMA_SETTINGS}
            style={{ position: 'absolute', inset: 0 }}
          >
            <SigmaContent />
          </SigmaContainer>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-navy-900">Cognitive Graph</h1>
          <p className="text-sm text-slate-500 mt-1">
            Interactive knowledge graph explorer &mdash; entities, relationships, and
            semantic topology.
          </p>
        </div>
        <button
          onClick={() => setFullscreen(true)}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 hover:border-slate-300 transition-colors"
        >
          <Maximize2 size={14} />
          Fullscreen
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl px-4 py-3">
          {error}
        </div>
      )}

      <div
        className="bg-slate-900 rounded-xl border border-slate-200 shadow-sm overflow-hidden flex"
        style={{ height: '75vh', ...SIGMA_CSS_VARS }}
      >
        <GraphControlsPanel />
        <div className="flex-1 relative min-w-0">
          {!hasData && <GraphEmptyState />}
          <SigmaContainer
            graph={Graph}
            settings={SIGMA_SETTINGS}
            style={{ position: 'absolute', inset: 0 }}
          >
            <SigmaContent />
          </SigmaContainer>
        </div>
      </div>
    </div>
  );
}
